import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { getSuccessStories } from "@/lib/api";
import { Quote } from "lucide-react";

export default function SuccessStories() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    getSuccessStories().then((d) => setItems(d.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    timer.current = setInterval(() => {
      setActive((a) => (a + 1) % items.length);
    }, 5200);
    return () => clearInterval(timer.current);
  }, [items.length]);

  const cur = items[active];

  return (
    <section
      id="stories"
      data-testid="stories-section"
      className="relative py-28 lg:py-40 px-6 lg:px-10 bg-[#0A0A0A] border-t border-[#1c1c1c] overflow-hidden"
    >
      <div className="absolute right-[-20%] top-1/2 -translate-y-1/2 w-[700px] h-[700px] spotlight-bl pointer-events-none opacity-70" />

      <div className="max-w-[1400px] mx-auto relative">
        <div className="flex items-baseline gap-4 mb-14">
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E31E24]">
            (05) · Family Stories
          </span>
          <span className="flex-1 h-px bg-white/15 mt-1" />
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-white/50 hidden md:block">
            Success Stories
          </span>
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="col-span-12 lg:col-span-5 font-anton uppercase leading-[0.9] text-[10vw] md:text-[7vw] lg:text-[5vw]"
          >
            Customers we <span className="font-cormorant italic font-medium text-[#E31E24]">call family.</span>
          </motion.h2>

          <div className="col-span-12 lg:col-span-7">
            {/* Quote card */}
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
              data-testid="story-active-card"
              className="border border-white/15 bg-[#141414]/60 backdrop-blur-md p-8 lg:p-12 relative min-h-[280px]"
            >
              <Quote className="text-[#E31E24] mb-4" size={28} strokeWidth={1.5} />
              <p className="font-cormorant italic text-white text-2xl lg:text-3xl leading-snug mb-8 max-w-2xl">
                {cur?.quote}
              </p>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="font-anton uppercase text-xl lg:text-2xl">{cur?.name}</div>
                  <div className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-white/50 mt-1">
                    {cur?.city}
                  </div>
                </div>
                <div className="border border-[#E31E24]/60 px-4 py-2">
                  <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E31E24]">
                    Sourced
                  </div>
                  <div className="font-anton uppercase text-sm lg:text-base mt-1">{cur?.car}</div>
                </div>
              </div>
            </motion.div>

            {/* Dots + labels row */}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              {items.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`story-dot-${i}`}
                  className={`group flex items-center gap-2 px-2.5 py-1.5 transition-colors ${
                    active === i ? "text-white" : "text-white/60 hover:text-white/70"
                  }`}
                  aria-label={`Show story from ${s.name}`}
                >
                  <span
                    className={`inline-block h-px transition-all duration-500 ${
                      active === i ? "bg-[#E31E24] w-10" : "bg-white/25 w-5 group-hover:w-8"
                    }`}
                  />
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.26em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Rotating horizontal strip */}
        <div className="mt-16 lg:mt-24 border-t border-white/12 pt-6 relative overflow-hidden">
          <div className="marquee-track font-anton uppercase text-[38px] lg:text-[56px] leading-none">
            {[...items, ...items, ...items].map((s, i) => (
              <span key={i} className="mx-6 lg:mx-10 inline-flex items-center gap-6">
                <span className="text-white/85">{s.name}</span>
                <span className="text-[#E31E24]">·</span>
                <span className="font-cormorant italic text-[#E31E24]">{s.car}</span>
                <span className="text-white/25">·</span>
                <span className="font-mono2 text-sm text-white/60 tracking-widest">
                  {s.city}
                </span>
                <span className="text-white/25 mx-4">/</span>
              </span>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0A0A0A] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0A0A0A] to-transparent" />
        </div>
      </div>
    </section>
  );
}
